/**
 * Editorial Engine — internal admin workspace for premium digital editions.
 * Route: /admin/editorial and /admin/editorial/materials/:materialId
 *
 * Sprint 01 foundation: structural editor + MASTER library + preview shell.
 * Deliberately not final visual design — that comes in Sprint 02.
 */
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Plus, Trash2, Copy, Eye, EyeOff, ArrowUp, ArrowDown, Monitor, Smartphone, HelpCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { HeroEditor, HeroRender } from "@/components/editorial/HeroMaster";
import { EditorialStoryEditor, EditorialStoryRender } from "@/components/editorial/EditorialStoryMaster";
import { VisualStoryEditor, VisualStoryRender } from "@/components/editorial/VisualStoryMaster";
import { RecipeEditor, RecipeRender } from "@/components/editorial/RecipeMaster";
import { HistoricalEditorialEditor, HistoricalEditorialRender } from "@/components/editorial/HistoricalEditorialMaster";
import { MacroSensoryEditor, MacroSensoryRender } from "@/components/editorial/MacroSensoryMaster";
import { PortraitDialogueEditor, PortraitDialogueRender } from "@/components/editorial/PortraitDialogueMaster";
import { MacroEditor, MacroRender } from "@/components/editorial/MacroMaster";
import { ProcessStoryEditor, ProcessStoryRender } from "@/components/editorial/ProcessStoryMaster";
import { StepByStepEditor, StepByStepRender } from "@/components/editorial/StepByStepMaster";
import { EditorialPauseEditor, EditorialPauseRender } from "@/components/editorial/EditorialPauseMaster";
import { KnowledgeEditor, KnowledgeRender } from "@/components/editorial/KnowledgeMaster";
import { useAuth } from "@/context/AuthContext";
import OwnerMaterialLifecycleActions from "@/components/editorial-v2/OwnerMaterialLifecycleActions";
import { Library, CakeSlice, GraduationCap, Camera, Cookie, ArrowRight, Search, Settings2 } from "lucide-react";
import {
  EDITORIAL_WORKSPACES, OPEN_LIBRARY_EDITORIAL_SECTIONS,
  editorialVolumesForWorkspace, isTechnicalEditorialEdition,
  isSafeEditorialCreationTarget, canOfferEditorialDraftDelete,
} from "@/lib/editorialWorkspaceNavigationV1.mjs";
import { ALMOND_PRODUCT_ID } from "@/lib/almondPublicationCatalogV1.mjs";
import EditorialWorkspaceHub from "@/pages/admin/EditorialWorkspaceHub";
import "@/styles/editorial-admin.css";

const ED = "/admin/editorial";
const EDITORIAL_STATUS_LABEL = { draft: "Черновик", published: "Опубликовано", archived: "Завершение действия" };
const openLibraryLifecycleVenue = volume => !volume.product_id
  && ["open-library", "open-library-author-intro"].includes(volume.slug);
const ownerLifecycleVenue = volume => volume.product_id === "78917116-d963-45dd-946c-7797db939790"
  || !volume.product_id;
const plainOpenLibraryDraft = (volume, material) => openLibraryLifecycleVenue(volume)
  && material.status === "draft"
  && !material.open_library_last_action && !material.owner_lifecycle_revision
  && !material.published_snapshot_id;

const MASTER_USAGE_HELP = {
  HERO: "Начало большого материала, главы или программы.",
  EDITORIAL_STORY: "Авторский рассказ, длинная история или эссе.",
  VISUAL_STORY: "Материал, где основную роль играет серия фотографий.",
  MACRO: "Один сильный визуальный кадр и короткая мысль.",
  MACRO_SENSORY: "Вкус, аромат, текстура и другие ощущения.",
  PORTRAIT_DIALOGUE: "Герой, интервью, вопросы и ответы.",
  PROCESS_STORY: "Объяснение процесса и логики приготовления.",
  STEP_BY_STEP: "Чёткая инструкция по шагам.",
  EDITORIAL_PAUSE: "Короткая мысль или цитата между крупными смысловыми блоками. Фото не обязательно.",
  RECIPE: "Ингредиенты, количество и технология приготовления.",
  KNOWLEDGE: "Таблица, сравнение, карта или другая структурированная справочная информация. Фото не обязательно.",
  HISTORICAL_EDITORIAL: "Происхождение, место, время, традиции и культурный контекст.",
};

const MASTER_FIELD_HELP = {
  HERO: [
    ["Надзаголовок", "Короткая рубрика над основным заголовком."],
    ["Заголовок", "Главное название этого разворота."],
    ["Подзаголовок", "Короткая фраза, поясняющая тему."],
    ["Короткий лид", "Небольшое введение перед основным содержанием."],
  ],
  EDITORIAL_STORY: [
    ["Вступление", "Небольшое введение перед основным рассказом."],
    ["Текстовые блоки", "Абзацы длинной авторской истории или эссе."],
    ["Акцентная фраза", "Короткая мысль, которую нужно визуально выделить."],
  ],
  VISUAL_STORY: [
    ["Текстовые блоки", "Короткий текст, который сопровождает серию фотографий."],
    ["Акцентная цитата", "Короткая мысль, которую нужно визуально выделить."],
    ["Фотографии", "Расположите изображения в нужном порядке; итоговую композицию проверьте в Preview."],
  ],
  MACRO: [
    ["Короткая мысль", "Одна-две фразы, которые поддерживают сильный крупный кадр."],
    ["Подпись", "Автор, источник или короткий контекст мысли."],
  ],
  MACRO_SENSORY: [
    ["Сенсорные ноты", "Опишите вкус, аромат, текстуру, внешний вид или послевкусие."],
    ["Акцентная фраза", "Короткий итог, который объединяет наблюдения."],
  ],
  PORTRAIT_DIALOGUE: [
    ["Введение", "Коротко представьте героя перед разговором."],
    ["Вопросы и ответы", "Каждый вопрос и ответ сохраняется отдельной парой."],
    ["Авторская цитата", "Одна фраза героя, задающая тон разговору."],
  ],
  PROCESS_STORY: [
    ["Фазы процесса", "Объясните, что происходит на каждом этапе и почему это важно."],
    ["Ключевой параметр", "Температура, время, состояние или другой ориентир автора."],
  ],
  STEP_BY_STEP: [
    ["Шаги", "Опишите последовательные действия в том порядке, в котором их выполняет читатель."],
    ["Ключевой параметр", "Короткий ориентир: время, температура или признак готовности."],
  ],
  EDITORIAL_PAUSE: [
    ["Цитата", "Короткая мысль между крупными смысловыми блоками."],
    ["Автор или подпись", "Источник цитаты или короткая авторская подпись."],
  ],
  RECIPE: [
    ["Выход", "Количество порций или итоговый вес готового рецепта."],
    ["Форма / размер", "Размер формы или другой рабочий формат."],
    ["Важное", "Критическая точка, без которой результат может измениться."],
  ],
  KNOWLEDGE: [
    ["Таблица", "Добавляйте столбцы и строки для сравнения или справочных данных."],
    ["Примечание", "Короткое пояснение под таблицей, если оно необходимо."],
  ],
  HISTORICAL_EDITORIAL: [
    ["Период", "Время или эпоха, к которой относится материал."],
    ["Место", "Страна, город или регион исторического контекста."],
    ["Источник", "Автор, издание или год, если они известны."],
  ],
};

// ---------- Product-first Editor entrance ----------
export function EditorialHome() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  return user?.role === "owner" && params.get("technical") === "1"
    ? <LegacyEditorialHome />
    : <EditorialWorkspaceHub />;
}

// Original technical edition/volume list remains available to Owner for QA
// and historical cleanup. The publication and draft safety rules are unchanged.
function LegacyEditorialHome() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "owner" || user?.role === "administrator";
  const [editions, setEditions] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(null);
  const [draftToDelete, setDraftToDelete] = useState(null);
  const [deletingDraft, setDeletingDraft] = useState(false);
  const [lifecycleEnabled, setLifecycleEnabled] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [workspaceParams, setWorkspaceParams] = useSearchParams();
  const selectedSpaceKey = workspaceParams.get("space") || "";
  const activeSpace = EDITORIAL_WORKSPACES.find((item) => item.key === selectedSpaceKey) || null;
  const technicalMode = user?.role === "owner" && workspaceParams.get("technical") === "1";
  const [materialSearch, setMaterialSearch] = useState("");
  const [materialFilter, setMaterialFilter] = useState("all");
  const allVolumes = useMemo(() => editions.flatMap((edition) =>
    (edition.volumes || []).map((volume) => ({ ...volume, edition }))
  ), [editions]);
  const workspaceVolumes = useMemo(() => activeSpace
    ? editorialVolumesForWorkspace(activeSpace.key, allVolumes, products) : [],
    [activeSpace, allVolumes, products]);
  const safeCreationVolumes = useMemo(() => workspaceVolumes.filter((volume) =>
    isSafeEditorialCreationTarget(selectedSpaceKey, volume, editions, products)
  ), [workspaceVolumes, selectedSpaceKey, editions, products]);
  const workspaceMaterials = useMemo(() => workspaceVolumes.flatMap((volume) =>
    (volume.materials || []).map((material) => ({ ...material, sourceVolume: volume }))
  ).filter((material) => {
    if (materialFilter === "draft" && material.status !== "draft") return false;
    if (materialFilter === "published" && material.status !== "published" && !material.published_snapshot_id) return false;
    const term = materialSearch.trim().toLocaleLowerCase("ru-RU");
    return !term || String(material.title || "").toLocaleLowerCase("ru-RU").includes(term);
  }), [workspaceVolumes, materialFilter, materialSearch]);
  const setWorkspace = (key) => {
    const next = new URLSearchParams(workspaceParams);
    next.delete("technical");
    if (key) next.set("space", key); else next.delete("space");
    setWorkspaceParams(next, { replace: true });
    setMaterialSearch("");
    setMaterialFilter("all");
  };
  const setTechnicalMode = (enabled) => {
    const next = new URLSearchParams(workspaceParams);
    next.delete("space");
    if (enabled) next.set("technical", "1"); else next.delete("technical");
    setWorkspaceParams(next, { replace: true });
  };


  const load = useCallback(async () => {
    try {
      // Load products only for admins — the picker is admin-only.
      // Editors still see the linked product NAME (read-only) via the
      // `products` list resolved here when available; if the request fails
      // (role check), the UI silently falls back to just the product_id.
      const requests = [
        api.get(`${ED}/editions`),
        api.get(`${ED}/volumes`),
        api.get(`${ED}/materials`),
      ];
      if (isAdmin) requests.push(api.get(`/admin/products`).catch(() => ({ data: [] })));
      if (user?.role === "owner") requests.push(api.get(`${ED}/materials/owner-lifecycle/capabilities`).catch(() => ({ data: {} })));
      const [ed, vol, mat, prd, capability] = await Promise.all(requests);
      setLifecycleEnabled(capability?.data?.enabled === true);
      const volsByEd = {};
      (vol.data || []).forEach((v) => { (volsByEd[v.edition_id] ||= []).push(v); });
      const matsByVol = {};
      const volumeById = new Map((vol.data || []).map(v => [v.id, v]));
      (mat.data || []).filter(m => m.status !== "archived" || m.owner_lifecycle_operation
        || (showArchive && openLibraryLifecycleVenue(volumeById.get(m.volume_id) || {})))
        .forEach((m) => { (matsByVol[m.volume_id] ||= []).push(m); });
      setEditions((ed.data || []).map((e) => ({
        ...e,
        volumes: (volsByEd[e.id] || []).map((v) => ({
          ...v, materials: matsByVol[v.id] || [],
        })),
      })));
      setProducts((prd?.data || []));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [isAdmin, user?.role, showArchive]);
  useEffect(() => { load(); }, [load]);

  // Lookup helper: resolve linked product name for a volume regardless of role.
  const productNameById = useMemo(() => {
    const m = new Map();
    products.forEach((p) => m.set(p.id, p.title || p.slug));
    return m;
  }, [products]);

  const setVolumeProduct = async (volumeId, productId) => {
    try {
      await api.patch(`${ED}/volumes/${volumeId}`, { product_id: productId || null });
      toast.success(productId ? "Продукт привязан" : "Ссылка на продукт снята");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submitCreate = async ({ kind, parentId, title, productId }) => {
    try {
      if (kind === "edition") {
        await api.post(`${ED}/editions`, { title, status: "draft" });
        toast.success("Издание создано");
      } else if (kind === "volume") {
        const { data: volume } = await api.post(`${ED}/volumes`, {
          edition_id: parentId, title, status: "draft",
        });
        if (isAdmin && productId) {
          await api.patch(`${ED}/volumes/${volume.id}`, { product_id: productId });
        }
        toast.success("Том создан");
      } else if (kind === "material") {
        const { data } = await api.post(`${ED}/materials`, {
          volume_id: parentId, title, status: "draft",
        });
        toast.success("Материал создан");
        setCreateDialog(null);
        nav(`/admin/editorial-v2/materials/${data.id}`, { state: { returnSpace: activeSpace?.key || null } });
        return;
      }
      setCreateDialog(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteSelectedDraft = async () => {
    if (!draftToDelete || deletingDraft) return;
    setDeletingDraft(true);
    try {
      if (draftToDelete.archiveMode) {
        const { data } = await api.post(
          `${ED}/materials/${encodeURIComponent(draftToDelete.id)}/archive-internal-draft`
        );
        if (data?.action !== "archived") throw new Error("Архивация не подтверждена");
        toast.success("Черновик перемещён в архив");
      } else {
        await api.delete(`${ED}/materials/${encodeURIComponent(draftToDelete.id)}`);
        toast.success("Черновик удалён");
      }
      setDraftToDelete(null);
      await load();
    } catch (error) {
      const reason = error?.response?.data?.detail;
      if (reason === "editorial_draft_has_publication" && !draftToDelete.archiveMode) {
        try {
          // A publication conflict is not automatically archivable. Show the
          // option only when the backend confirms an internal-only approval.
          const { data } = await api.get(
            `${ED}/materials/${encodeURIComponent(draftToDelete.id)}/archive-internal-draft/eligibility`
          );
          if (data?.eligible === true) {
            setDraftToDelete((current) => current ? { ...current, archiveMode: true } : current);
            return;
          }
        } catch {
          // No archive offer for published, scheduled or otherwise protected data.
        }
      }
      const message = reason === "editorial_draft_has_publication"
        ? "Архивация запрещена: материал связан с публикацией."
        : reason === "editorial_draft_changed_retry"
          ? "Черновик изменился. Обновите список перед удалением."
          : reason === "editorial_draft_protected_fixture"
            ? "Служебный материал защищён от удаления."
            : formatApiError(error);
      toast.error(message);
    } finally {
      setDeletingDraft(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="editorial-home">
      <header className="border-b border-espresso/10 bg-white/60 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center gap-3">
          <button onClick={() => activeSpace || technicalMode ? (activeSpace ? setWorkspace(null) : setTechnicalMode(false)) : nav("/admin")} className="p-2 -ml-2 rounded-full hover:bg-sand/50"
            data-testid="editorial-back-admin">
            <ChevronLeft className="w-5 h-5 text-espresso" />
          </button>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">Anokina Studio</div>
            <h1 className="font-serif text-2xl text-espresso">{activeSpace ? activeSpace.title : technicalMode ? "Служебные материалы" : "Редакция"}</h1>
          </div>
          {technicalMode && user?.role === "owner" && <Button type="button" variant="outline"
            onClick={() => setShowArchive(value => !value)} data-testid="editorial-toggle-archive"
            aria-pressed={showArchive}>
            {showArchive ? "Скрыть архив" : "Показать архив"}
          </Button>}
          {technicalMode && <Button onClick={() => setCreateDialog({ kind: "edition" })} data-testid="editorial-new-edition">
            <Plus className="w-4 h-4 mr-1" /> Новое издание
          </Button>}
        </div>
      </header>


      <div className="max-w-6xl mx-auto px-5 py-8 space-y-6">
        {!technicalMode && !activeSpace && (
          <section data-testid="editorial-workspace-hub" className="space-y-7">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Рабочее пространство</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-espresso mt-2">Мои публикации</h2>
              <p className="text-sm text-muted-foreground mt-2">Выберите пространство. Внутри — только его материалы и редакционные инструменты.</p>
            </div>
            {[["library", "Открытая библиотека"], ["programs", "Авторские программы"], ["workshops", "Мастерские"]].map(([group, heading]) => (
              <section key={group} className="space-y-3">
                <h3 className="font-serif text-xl text-espresso">{heading}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {EDITORIAL_WORKSPACES.filter((space) => space.group === group).map((space) => {
                    const Icon = space.key === "open-library" ? Library
                      : space.key === "almond" ? CakeSlice
                      : space.key === "media-studio" ? Camera
                      : space.key === "gingerbread" ? Cookie : GraduationCap;
                    const volumes = editorialVolumesForWorkspace(space.key, allVolumes, products);
                    const total = volumes.reduce((sum, volume) => sum + (volume.materials || []).length, 0);
                    return <button key={space.key} type="button" onClick={() => setWorkspace(space.key)}
                      data-testid={`editorial-workspace-${space.key}`}
                      className="min-h-[155px] rounded-2xl border hairline bg-white/80 p-5 text-left flex flex-col items-start gap-2 hover:bg-sand/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-espresso">
                      <span className="rounded-full bg-sand/60 p-3"><Icon size={20} className="text-espresso" /></span>
                      <span className="font-serif text-xl text-espresso">{space.title}</span>
                      <span className="text-xs text-muted-foreground">{space.description}</span>
                      <span className="text-[11px] text-muted-foreground mt-auto">{loading ? "Загрузка…" : total ? `Материалов: ${total}` : "Материалов пока нет"}</span>
                    </button>;
                  })}
                </div>
              </section>
            ))}
            {user?.role === "owner" && (
              <button type="button" onClick={() => setTechnicalMode(true)} data-testid="editorial-owner-technical"
                className="flex items-center gap-2 text-xs text-muted-foreground hover:text-espresso">
                <Settings2 size={15} /> Служебные материалы (только владелец)
              </button>
            )}
          </section>
        )}
        {!technicalMode && activeSpace && (
          <section data-testid="editorial-workspace-content" className="space-y-5">
            <button type="button" onClick={() => setWorkspace(null)} data-testid="editorial-back-workspaces"
              className="rounded-xl border hairline bg-white/80 px-4 py-2 text-sm text-espresso hover:bg-sand/30">← Все пространства</button>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Редакция / {activeSpace.title}</div>
                <h2 className="font-serif text-3xl text-espresso mt-1">Публикации</h2>
              </div>
              {safeCreationVolumes.length === 1 && <Button type="button"
                data-testid="editorial-workspace-new-material"
                onClick={() => setCreateDialog({ kind: "material", parentId: safeCreationVolumes[0].id })}
                className="bg-espresso text-white hover:bg-espressoDark rounded-xl"><Plus className="w-4 h-4 mr-2"/> Создать материал</Button>}
            </div>
            {activeSpace.key === "open-library" && (
              <div className="flex flex-wrap gap-2" data-testid="editorial-open-library-sections">
                {OPEN_LIBRARY_EDITORIAL_SECTIONS.map((section) => (
                  <span key={section.id} className="text-xs rounded-full border hairline bg-white/60 px-3 py-2">{section.title}</span>
                ))}
              </div>
            )}
            {activeSpace.key === "almond" && (
              <div className="rounded-xl border hairline bg-white/75 p-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">Содержание Тома I и четыре пространства книги сохранены в существующем редакционном кабинете.</p>
                <button type="button" onClick={() => nav(`/admin/products/${ALMOND_PRODUCT_ID}?tab=content`)}
                  data-testid="editorial-almond-contents" className="rounded-xl bg-espresso text-white px-4 min-h-10 text-sm hover:bg-espressoDark">Открыть содержание Тома I →</button>
              </div>
            )}
            {workspaceVolumes.length === 0 && !loading && (
              <div className="rounded-2xl border hairline bg-white/70 px-5 py-6">
                <p className="font-serif text-xl text-espresso">Редакционные материалы ещё не подключены</p>
                <p className="text-sm text-muted-foreground mt-2">В этом пространстве пока нет подтверждённой связи с редакционным томом. Ничего создавать или привязывать автоматически не будем, чтобы не получить дубликаты.</p>
              </div>
            )}
            {workspaceVolumes.length > 0 && safeCreationVolumes.length === 0 && (
              <p className="rounded-xl border hairline bg-sand/20 p-4 text-sm text-espresso/80">Найдены ранее созданные материалы. Редактирование доступно, но создание новых материалов здесь временно закрыто до проверки места публикации.</p>
            )}
            {safeCreationVolumes.length > 1 && (
              <div className="rounded-xl bg-sand/25 border hairline p-4 space-y-2">
                <p className="text-sm text-espresso">Выберите раздел для нового материала:</p>
                {safeCreationVolumes.map((volume) => (
                  <button key={volume.id} type="button" onClick={() => setCreateDialog({ kind: "material", parentId: volume.id })}
                    className="block text-sm text-espresso underline underline-offset-4">{volume.title}</button>
                ))}
              </div>
            )}
            {workspaceVolumes.length > 0 && (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex-1 min-w-[180px] flex items-center gap-2 rounded-xl border hairline bg-white/80 px-3 h-11">
                    <Search size={17} className="text-muted-foreground"/>
                    <input type="search" placeholder="Поиск по названию" aria-label="Поиск по материалам"
                      value={materialSearch} onChange={(event) => setMaterialSearch(event.target.value)}
                      className="min-w-0 w-full bg-transparent outline-none text-sm"/>
                  </label>
                  <div className="flex gap-1 rounded-xl bg-sand/30 p-1">
                    {[["all", "Все"], ["published", "Опубликованные"], ["draft", "Черновики"]].map(([value, label]) => (
                      <button key={value} type="button" onClick={() => setMaterialFilter(value)}
                        aria-pressed={materialFilter === value}
                        className={`rounded-lg min-h-9 px-3 text-xs ${materialFilter === value ? "bg-white shadow-sm text-espresso" : "text-espresso/70"}`}>{label}</button>
                    ))}
                  </div>
                </div>
                {workspaceMaterials.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">По выбранным условиям материалов нет.</p>}
                <div className="space-y-2" data-testid="editorial-workspace-materials">
                  {workspaceMaterials.map((material) => (
                    <article key={material.id} className="rounded-xl border hairline bg-white/85 px-4 py-3 flex flex-wrap sm:flex-nowrap items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-serif text-lg text-espresso break-words">{material.title}</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          {material.published_snapshot_id && material.status === "draft" ? "Опубликованная версия и рабочий черновик"
                            : material.status === "published" ? "Опубликовано" : material.status === "archived" ? "Архив" : "Черновик"}
                          {workspaceVolumes.length > 1 ? ` · ${material.sourceVolume.title}` : ""}
                        </p>
                      </div>
                      <button type="button" disabled={!!material.owner_lifecycle_operation || material.status === "archived"}
                        data-testid={`editorial-workspace-edit-${material.id}`}
                        onClick={() => nav(`/admin/editorial-v2/materials/${material.id}`, { state: { returnSpace: activeSpace.key } })}
                        className="rounded-lg border hairline px-4 min-h-10 text-sm text-espresso hover:bg-sand/25 disabled:opacity-50">Редактировать</button>
                      {canOfferEditorialDraftDelete(material, user?.role) && !isTechnicalEditorialEdition(material.sourceVolume.edition) && (
                        <button type="button" onClick={() => setDraftToDelete({ id: material.id, title: material.title })}
                          data-testid={`editorial-workspace-delete-${material.id}`}
                          className="rounded-lg border border-red-200 px-3 min-h-10 text-sm text-red-700 hover:bg-red-50">Удалить</button>
                      )}
                    </article>
                  ))}
                </div>
              </>
            )}
          </section>
        )}
        {technicalMode && <div className="flex items-center gap-3 rounded-xl bg-sand/25 px-4 py-3">
          <span className="text-sm text-espresso">Служебная редакционная структура. Не удаляйте QA и E2E материалы без аудита связей.</span>
          <Button variant="outline" onClick={() => setTechnicalMode(false)}>К публикациям</Button>
        </div>}
        {loading && <div className="opacity-60 text-sm">Загрузка…</div>}
        {technicalMode && !loading && editions.length === 0 && (
          <div className="p-16 text-center opacity-70">
            <div className="font-serif text-2xl mb-2">Изданий пока нет</div>
            <div className="text-sm mb-6">Создайте первое цифровое издание, чтобы начать.</div>
            <Button onClick={() => setCreateDialog({ kind: "edition" })}><Plus className="w-4 h-4 mr-1" /> Создать издание</Button>
          </div>
        )}
        {technicalMode && editions.map((e) => (
          <div key={e.id} className="rounded-2xl bg-white/60 border hairline p-5"
               data-testid={`editorial-edition-${e.id}`}>
            <div className="flex items-baseline justify-between gap-3 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest opacity-60">Издание</div>
                <div className="font-serif text-2xl text-espresso">{e.title}</div>
                {e.subtitle && <div className="italic opacity-70 text-sm">{e.subtitle}</div>}
              </div>
              <Button variant="outline" size="sm" onClick={() => setCreateDialog({ kind: "volume", parentId: e.id })}
                data-testid={`editorial-new-volume-${e.id}`}>
                <Plus className="w-4 h-4 mr-1" /> Том
              </Button>
            </div>
            {(e.volumes || []).length === 0 && (
              <div className="text-sm opacity-60 pl-2">Томов пока нет.</div>
            )}
            {(e.volumes || []).map((v) => (
              <div key={v.id} className="mt-3 pl-3 border-l-2 border-espresso/15"
                   data-testid={`editorial-volume-${v.id}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest opacity-60">Том</div>
                    <div className="font-serif text-xl text-espresso">{v.title}</div>
                    {/* Linked product — name shown to everyone; picker shown to admin only. */}
                    {isAdmin ? (
                      <div className="mt-1 flex items-center gap-2 text-xs" data-testid={`editorial-volume-product-picker-${v.id}`}>
                        <span className="opacity-60">Продукт:</span>
                        <select
                          value={v.product_id || ""}
                          onChange={(ev) => setVolumeProduct(v.id, ev.target.value || null)}
                          className="h-7 rounded border border-espresso/20 bg-white px-2"
                          data-testid={`editorial-volume-product-select-${v.id}`}
                        >
                          <option value="">— не привязан —</option>
                          {products.map((p) => (
                            <option key={p.id} value={p.id}>{p.title || p.slug}</option>
                          ))}
                        </select>
                      </div>
                    ) : v.product_id ? (
                      <div
                        className="mt-1 text-xs opacity-70"
                        data-testid={`editorial-volume-product-label-${v.id}`}
                      >
                        Продукт: {productNameById.get(v.product_id) || v.product_id}
                      </div>
                    ) : null}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setCreateDialog({ kind: "material", parentId: v.id })}
                    data-testid={`editorial-new-material-${v.id}`}>
                    <Plus className="w-4 h-4 mr-1" /> Материал
                  </Button>
                </div>
                {(v.materials || []).length === 0 && (
                  <div className="text-sm opacity-60 mt-2">Материалов пока нет.</div>
                )}
                <ul className="mt-2 space-y-1.5">
                  {(v.materials || []).map((m) => (
                    <li key={m.id} className={`flex items-stretch gap-2 ${lifecycleEnabled && user?.role === "owner" && ownerLifecycleVenue(v) ? "flex-col sm:flex-row" : "flex-row"}`}>
                      <button disabled={!!m.owner_lifecycle_operation || m.status === "archived"} onClick={() => nav(`/admin/editorial-v2/materials/${m.id}`)}
                        className="flex-1 min-w-0 text-left rounded-lg bg-white border hairline p-3 hover:bg-sand/30 flex items-center gap-3"
                        data-testid={`editorial-material-${m.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-serif text-lg text-espresso">{m.title}</div>
                          {m.subtitle && <div className="text-xs opacity-60">{m.subtitle}</div>}
                        </div>
                        <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded ${
                          m.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-600"
                        }`}>{EDITORIAL_STATUS_LABEL[m.status] || m.status}</span>
                      </button>
                      {lifecycleEnabled && user?.role === "owner" && ownerLifecycleVenue(v) && !["0f26cb7d-f6ce-484c-b9a0-596b33e60419", "9c60f83f-b5ab-438b-b27e-09f41480a620"].includes(m.id) && !m.e2e_fixture && !m.qa_fixture ? (
                        <>
                          <OwnerMaterialLifecycleActions material={m} onDone={load}
                            openLibrary={openLibraryLifecycleVenue(v)} />
                          {isAdmin && plainOpenLibraryDraft(v, m) && (
                            <button type="button"
                              onClick={() => setDraftToDelete({ id: m.id, title: m.title })}
                              className="shrink-0 rounded-lg border border-espresso/15 bg-white px-3 text-espresso/60 hover:text-red-700 hover:border-red-700/40"
                              title="Удалить черновик без публикационной истории"
                              aria-label={`Удалить черновик: ${m.title}`}
                              data-testid={`editorial-delete-draft-${m.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </>
                      ) : isAdmin && m.status === "draft" && m.id !== "0f26cb7d-f6ce-484c-b9a0-596b33e60419"
                        && !m.e2e_fixture && !m.qa_fixture && !m.published_snapshot_id
                        && (!openLibraryLifecycleVenue(v) || plainOpenLibraryDraft(v, m)) && (
                        <button type="button"
                          onClick={() => setDraftToDelete({ id: m.id, title: m.title })}
                          className="shrink-0 rounded-lg border border-espresso/15 bg-white px-3 text-espresso/60 hover:text-red-700 hover:border-red-700/40"
                          title="Удалить черновик" aria-label={`Удалить черновик: ${m.title}`}
                          data-testid={`editorial-delete-draft-${m.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))}
      </div>
      <Dialog open={!!draftToDelete} onOpenChange={(open) => { if (!open && !deletingDraft) setDraftToDelete(null); }}>
        <DialogContent data-testid="editorial-delete-draft-dialog">
          <DialogHeader>
            <DialogTitle>{draftToDelete?.archiveMode ? "Переместить черновик в архив?" : "Удалить черновик?"}</DialogTitle>
            <DialogDescription>
              {draftToDelete?.archiveMode
                ? <>«{draftToDelete?.title}» имеет внутреннюю историю утверждения. Удаление недоступно.
                    Архивирование уберёт его из рабочего списка, сохранив материал, развороты и историю.
                    Это не публикация в Томе.</>
                : <>«{draftToDelete?.title}» будет удалён вместе с его черновыми разворотами.
                    Восстановить удалённый черновик нельзя. Опубликованные материалы и MASTER защищены.</>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={deletingDraft}
              onClick={() => setDraftToDelete(null)}>Отмена</Button>
            <Button type="button" disabled={deletingDraft} onClick={deleteSelectedDraft}
              data-testid="editorial-delete-draft-confirm"
              className="bg-red-700 hover:bg-red-800">
              {deletingDraft ? "Сохранение…" : draftToDelete?.archiveMode ? "Переместить в архив" : "Удалить черновик"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <EditorialCreateDialog
        config={createDialog}
        products={isAdmin ? products : []}
        onClose={() => setCreateDialog(null)}
        onSubmit={submitCreate}
      />
    </div>
  );
}

function EditorialCreateDialog({ config, products, onClose, onSubmit }) {
  const [title, setTitle] = useState("");
  const [productId, setProductId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTitle(""); setProductId(""); setBusy(false); }, [config]);
  if (!config) return null;
  const copy = {
    edition: {
      heading: "Новое издание", label: "Название издания",
      help: "Это название объединяет тома и материалы внутри Редакции.",
    },
    volume: {
      heading: "Новый том", label: "Название тома",
      help: "Например: Том I «Миндаль».",
    },
    material: {
      heading: "Новый материал", label: "Название материала",
      help: "Это название будет видно в списке Редакции. Заголовок публикации задаётся внутри материала.",
    },
  }[config.kind];
  const submit = async () => {
    const clean = title.trim();
    if (!clean) { toast.error(`Введите ${copy.label.toLowerCase()}`); return; }
    setBusy(true);
    try { await onSubmit({ ...config, title: clean, productId }); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid={`editorial-create-${config.kind}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-espresso">{copy.heading}</DialogTitle>
          <DialogDescription>{copy.help}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">{copy.label}</label>
            <Input autoFocus value={title} onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              className="mt-1 h-11 rounded-xl" data-testid="editorial-create-title" />
          </div>
          {config.kind === "volume" && products.length > 0 && (
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Связанный продукт · необязательно</label>
              <select value={productId} onChange={(e) => setProductId(e.target.value)}
                className="mt-1 h-11 w-full rounded-xl border border-espresso/20 bg-white px-3 text-sm"
                data-testid="editorial-create-volume-product">
                <option value="">Не привязывать сейчас</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.title || p.slug}</option>)}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">Привязку можно изменить позже в карточке тома.</p>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose}>Отмена</Button>
          <Button onClick={submit} disabled={busy} data-testid="editorial-create-submit">
            {busy ? "Создание…" : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Material editor ----------
const MASTER_CACHE_KEY = "editorial_master_types_cache";

export function EditorialMaterialEditor() {
  const { materialId } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [material, setMaterial] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [masters, setMasters] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(MASTER_CACHE_KEY) || "[]"); }
    catch { return []; }
  });
  const [saveState, setSaveState] = useState("idle"); // idle|saving|saved|error
  const [openLibrary, setOpenLibrary] = useState(false);
  const [preview, setPreview] = useState(searchParams.get("preview") || null); // null|desktop|mobile
  const [workspaceMode, setWorkspaceMode] = useState(searchParams.get("preview") ? "preview" : "content");
  const [editingMode, setEditingMode] = useState("content"); // content|design
  const [previewData, setPreviewData] = useState(null);
  const [previewReady, setPreviewReady] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [continueBusyId, setContinueBusyId] = useState(null);
  const continueBusyRef = useRef(false);
  const debounceRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [m, bs, mt] = await Promise.all([
        api.get(`${ED}/materials/${materialId}`),
        api.get(`${ED}/materials/${materialId}/blocks`),
        api.get(`${ED}/master-types`),
      ]);
      setMaterial(m.data);
      setBlocks(bs.data || []);
      setPreviewReady(
        m.data.previewed_revision !== null
        && m.data.previewed_revision !== undefined
        && m.data.previewed_revision === m.data.draft_revision
      );
      setMasters(mt.data || []);
      try { sessionStorage.setItem(MASTER_CACHE_KEY, JSON.stringify(mt.data || [])); } catch {}
    } catch (e) { toast.error(formatApiError(e)); }
  }, [materialId]);
  useEffect(() => { load(); }, [load]);

  const invalidatePreview = () => {
    setPreviewReady(false);
    setPreviewData(null);
  };

  const keepScroll = (update) => {
    const left = window.scrollX;
    const top = window.scrollY;
    update();
    requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(left, top)));
  };
  const openActive = (blockId) => keepScroll(() => setActiveBlockId(blockId));
  const closeActive = (blockId) => keepScroll(() => setActiveBlockId((cur) => (cur === blockId ? null : cur)));
  const toggleActive = (blockId) => keepScroll(() => setActiveBlockId((cur) => (cur === blockId ? null : blockId)));

  // Debounced autosave of material title / subtitle
  const scheduleMaterialSave = (patch) => {
    invalidatePreview();
    setMaterial((m) => ({ ...m, ...patch }));
    setSaveState("saving");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        await api.patch(`${ED}/materials/${materialId}`, patch);
        setSaveState("saved");
        setTimeout(() => setSaveState("idle"), 1500);
      } catch (e) { setSaveState("error"); toast.error(formatApiError(e)); }
    }, 700);
  };

  // Immediate block operations (each already atomic)
  const patchBlock = async (bid, patch) => {
    invalidatePreview();
    setBlocks((bs) => bs.map((b) => (b.id === bid ? { ...b, ...patch } : b)));
    setSaveState("saving");
    try {
      await api.patch(`${ED}/blocks/${bid}`, patch);
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 1200);
    } catch (e) { setSaveState("error"); toast.error(formatApiError(e)); }
  };
  const debouncedPatchBlock = (bid, patch) => {
    invalidatePreview();
    setBlocks((bs) => bs.map((b) => (b.id === bid ? { ...b, ...patch } : b)));
    setSaveState("saving");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => patchBlock(bid, patch), 700);
  };

  const addBlock = async (master_type, initialContent, opts = {}) => {
    invalidatePreview();
    try {
      const { data } = await api.post(`${ED}/materials/${materialId}/blocks`, {
        material_id: materialId,
        master_type,
        content: initialContent || { headline: "" },
        is_visible: true,
      });
      setBlocks((bs) => [...bs, data]);
      setOpenLibrary(false);
      setActiveBlockId(data.id);
      toast.success(opts.successMessage || "Разворот добавлен");
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    }
  };

  const continueHeroLeftover = async (blockId, leftoverText) => {
    const clean = (leftoverText || "").trim();
    if (!clean || continueBusyRef.current) return;
    continueBusyRef.current = true;
    setContinueBusyId(blockId);
    try {
      await addBlock(
        "EDITORIAL_STORY",
        { headline: "", extra: { paragraphs: [clean] } },
        { successMessage: "Текст перенесён в следующий разворот" }
      );
    } finally {
      continueBusyRef.current = false;
      setContinueBusyId(null);
    }
  };

  const quickAddMaster = (masterType) => addBlock(masterType);

  const move = async (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= blocks.length) return;
    invalidatePreview();
    const list = blocks.slice();
    [list[idx], list[to]] = [list[to], list[idx]];
    // renumber sort_order
    const items = list.map((b, i) => ({ id: b.id, sort_order: i }));
    setBlocks(list.map((b, i) => ({ ...b, sort_order: i })));
    try {
      await api.post(`${ED}/materials/${materialId}/blocks/reorder`, { items });
      setSaveState("saved"); setTimeout(() => setSaveState("idle"), 1200);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const duplicate = async (bid) => {
    invalidatePreview();
    try {
      await api.post(`${ED}/blocks/${bid}/duplicate`);
      toast.success("Разворот продублирован");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (bid) => {
    invalidatePreview();
    try {
      await api.delete(`${ED}/blocks/${bid}`);
      setBlocks((bs) => bs.filter((b) => b.id !== bid));
      setActiveBlockId((cur) => (cur === bid ? null : cur));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleVisible = (b) => patchBlock(b.id, { is_visible: !b.is_visible });

  const publish = async () => {
    try {
      const { data } = await api.post(`${ED}/materials/${materialId}/publish`);
      setMaterial(data);
      toast.success("Опубликовано");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setPreviewMode = async (mode, returnMode = editingMode) => {
    if (!mode) {
      setPreview(null);
      setEditingMode(returnMode);
      setWorkspaceMode(returnMode);
      const next = new URLSearchParams(searchParams);
      next.delete("preview");
      setSearchParams(next, { replace: true });
      return;
    }
    if (saveState === "saving") {
      toast.info("Дождитесь сохранения текущих изменений");
      return;
    }
    try {
      const { data } = await api.get(`${ED}/materials/${materialId}/preview`);
      setPreviewData(data);
      setPreviewReady(true);
      setMaterial(data.material);
      setPreview(mode);
      setWorkspaceMode("preview");
    } catch (e) {
      toast.error(formatApiError(e));
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("preview", mode);
    setSearchParams(next, { replace: true });
  };

  const setWorkspaceEditMode = (mode) => {
    setEditingMode(mode);
    if (preview) setPreviewMode(null, mode);
    else setWorkspaceMode(mode);
  };

  if (!material) return <div className="min-h-screen bg-cream" data-testid="editorial-loading" />;

  const saveLabel = {
    idle: "", saving: "Сохранение…", saved: "Сохранено", error: "Ошибка сохранения",
  }[saveState];

  return (
    <div className="min-h-screen bg-cream" data-testid="editorial-material-editor">
      <header className="border-b border-espresso/10 bg-white/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-3 md:px-5 py-2.5 flex items-center gap-2 md:gap-3 flex-wrap md:flex-nowrap">
          {preview ? (
            <div className="flex items-center gap-1.5 shrink-0 order-1" data-testid="editorial-preview-navigation">
              <Button variant="outline" size="sm" onClick={() => nav("/admin/editorial")}
                data-testid="editorial-preview-home">
                <ChevronLeft className="w-4 h-4 mr-1" /> В Редакцию
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPreviewMode(null)}
                data-testid="editorial-preview-back">
                <ChevronLeft className="w-4 h-4 mr-1" /> Вернуться к редактированию
              </Button>
            </div>
          ) : (
            <button onClick={() => nav("/admin/editorial")}
              className="p-2 -ml-2 rounded-full hover:bg-sand/50"
              data-testid="editorial-editor-back">
              <ChevronLeft className="w-5 h-5 text-espresso" />
            </button>
          )}
          <div className="flex-1 min-w-[160px] order-2">
            {preview ? (
              <div className="font-serif text-lg text-espresso truncate" data-testid="editorial-preview-material-title">
                {material.title}
              </div>
            ) : (
              <Input value={material.title || ""}
                onChange={(e) => scheduleMaterialSave({ title: e.target.value })}
                className="font-serif text-xl border-none bg-transparent px-0"
                data-testid="editorial-material-title" />
            )}
            <div className="text-[10px] uppercase tracking-widest opacity-60 flex items-center gap-3">
              <span>Статус: {EDITORIAL_STATUS_LABEL[material.status] || material.status}</span>
              {saveLabel && <span data-testid="editorial-save-state">· {saveLabel}</span>}
            </div>
          </div>
          <div className="order-3 flex rounded-xl border border-espresso/15 bg-white/75 p-1"
            data-testid="editorial-workspace-modes">
            <button type="button" onClick={() => setWorkspaceEditMode("content")}
              className={`rounded-lg px-3 py-1.5 text-xs ${workspaceMode === "content" ? "bg-espresso text-cream" : "text-espresso/70"}`}
              data-testid="editorial-mode-content">Содержание</button>
            <button type="button" onClick={() => setWorkspaceEditMode("design")}
              className={`rounded-lg px-3 py-1.5 text-xs ${workspaceMode === "design" ? "bg-espresso text-cream" : "text-espresso/70"}`}
              data-testid="editorial-mode-design">Оформление</button>
            <button type="button" onClick={() => setPreviewMode(preview || "desktop")}
              className={`rounded-lg px-3 py-1.5 text-xs ${workspaceMode === "preview" ? "bg-espresso text-cream" : "text-espresso/70"}`}
              disabled={saveState === "saving"}
              data-testid="editorial-mode-preview">Предпросмотр</button>
          </div>
          {workspaceMode === "preview" && (
            <>
              <Button variant={preview === "desktop" ? "default" : "outline"} size="sm" className="order-3"
                onClick={() => setPreviewMode("desktop")}
                disabled={saveState === "saving"}
                data-testid="editorial-preview-desktop">
                <Monitor className="w-4 h-4 mr-1" /> Desktop
              </Button>
              <Button variant={preview === "mobile" ? "default" : "outline"} size="sm" className="order-3"
                onClick={() => setPreviewMode("mobile")}
                disabled={saveState === "saving"}
                data-testid="editorial-preview-mobile">
                <Smartphone className="w-4 h-4 mr-1" /> Mobile
              </Button>
              <Button size="sm" className="order-3" onClick={() => setConfirmAction({ kind: "publish" })} disabled={!previewReady || saveState === "saving"}
                title={previewReady ? "Опубликовать текущую версию" : "Сначала откройте Preview текущей версии"}
                data-testid="editorial-publish">
                Опубликовать
              </Button>
            </>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-6">
        {workspaceMode !== "preview" && (
          <BlocksList blocks={blocks} masters={masters}
            onEditBlock={(bid, patch) => debouncedPatchBlock(bid, patch)}
            onMove={move} onDuplicate={duplicate}
            onRemove={(blockId) => setConfirmAction({ kind: "remove", blockId })}
            onToggleVisible={toggleVisible} onAdd={() => setOpenLibrary(true)}
            mode={editingMode} onPreview={() => setPreviewMode("desktop")}
            activeBlockId={activeBlockId} onOpenActive={openActive} onCloseActive={closeActive}
            onToggleActive={toggleActive}
            onContinueHeroLeftover={continueHeroLeftover} continueBusyId={continueBusyId}
            onQuickAddMaster={quickAddMaster} />
        )}
        {workspaceMode === "preview" && preview && (
          <PreviewShell blocks={previewData?.blocks || blocks}
              material={previewData?.material || material} masters={masters} mode={preview} />
        )}
      </div>

      {openLibrary && (
        <MasterLibrary masters={masters} onClose={() => setOpenLibrary(false)} onPick={addBlock} />
      )}
      <EditorialConfirmDialog
        action={confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={async () => {
          const action = confirmAction;
          setConfirmAction(null);
          if (action?.kind === "remove") await remove(action.blockId);
          if (action?.kind === "publish") await publish();
        }}
      />
    </div>
  );
}

function EditorialConfirmDialog({ action, onClose, onConfirm }) {
  if (!action) return null;
  const isPublish = action.kind === "publish";
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid={`editorial-confirm-${action.kind}`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-espresso">
            {isPublish ? "Опубликовать материал?" : "Удалить разворот?"}
          </DialogTitle>
          <DialogDescription>
            {isPublish
              ? "Будет опубликована версия, которую вы только что проверили в Preview."
              : "Разворот будет удалён безвозвратно. Остальные развороты не изменятся."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose}>Отмена</Button>
          <Button onClick={onConfirm} className={isPublish ? "" : "bg-red-700 hover:bg-red-800"}
            data-testid="editorial-confirm-submit">
            {isPublish ? "Опубликовать" : "Удалить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Spread thumbnail resolution ----------
function resolveSpreadThumbnail(content = {}) {
  const direct = [
    content.hero_url,
    content.hero_mobile_url,
    content.image_url,
    content.cover_url,
    content.photo_url,
  ];
  for (const url of direct) {
    if (typeof url === "string" && url.trim()) return url;
  }
  if (Array.isArray(content.media) && content.media.length) {
    const first = content.media[0];
    const url = first?.file_url || first?.url;
    if (typeof url === "string" && url.trim()) return url;
  }
  if (Array.isArray(content.photos) && content.photos.length) {
    const first = content.photos[0];
    const url = typeof first === "string" ? first : (first?.file_url || first?.url);
    if (typeof url === "string" && url.trim()) return url;
  }
  return null;
}

// ---------- Blocks list ----------
function BlocksList({
  blocks, masters, onEditBlock, onMove, onDuplicate, onRemove, onToggleVisible,
  onAdd, mode, onPreview, activeBlockId, onOpenActive, onCloseActive, onToggleActive,
  onContinueHeroLeftover, continueBusyId, onQuickAddMaster,
}) {
  const masterLabel = (key) => masters.find((m) => m.key === key)?.label || key;
  const spreadTitle = (b) => {
    const c = b.content || {};
    return c.headline || c.title || c.subtitle || masterLabel(b.master_type);
  };

  return (
    <div className="space-y-2" data-testid="editorial-blocks-list">
      {blocks.length === 0 && (
        <div className="p-10 text-center opacity-70 bg-white/50 rounded-2xl border hairline">
          <div className="font-serif text-2xl mb-2">Разворотов пока нет</div>
          <div className="text-sm mb-4">Добавьте первый разворот из библиотеки MASTER.</div>
          <Button onClick={onAdd} data-testid="editorial-empty-add">
            <Plus className="w-4 h-4 mr-1" /> Добавить разворот
          </Button>
        </div>
      )}
      {blocks.map((b, idx) => {
        const isActive = activeBlockId === b.id;
        const isLast = idx === blocks.length - 1;
        const thumb = resolveSpreadThumbnail(b.content || {});
        const showHeroNextStep =
          mode === "content" && isActive && isLast
          && b.master_type === "HERO" && Boolean((b.content || {}).headline);

        return (
          <div key={b.id}
            className={`editorial-compact-editor rounded-2xl bg-white border hairline ${b.is_visible ? "" : "opacity-60"}`}
            data-testid={`editorial-block-${b.id}`}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => onToggleActive(b.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggleActive(b.id);
                }
              }}
              className="flex items-center gap-3 p-3 cursor-pointer"
              data-testid={`editorial-block-header-${b.id}`}>
              <span className="text-[10px] tracking-widest opacity-50 w-6 shrink-0">
                {String(idx + 1).padStart(2, "0")}
              </span>
              {thumb && (
                <img src={thumb} alt="" className="h-9 w-9 shrink-0 rounded-lg object-cover" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2 py-0.5 rounded bg-espresso/8 text-[10px] uppercase tracking-widest shrink-0">
                    {masterLabel(b.master_type)}
                  </span>
                  {b.variant && <span className="text-[11px] opacity-60 shrink-0">{b.variant}</span>}
                  {!b.is_visible && <span className="text-[10px] italic opacity-70 shrink-0">скрыт</span>}
                </div>
                <div className="mt-0.5 truncate font-serif text-base text-espresso">
                  {spreadTitle(b)}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0" role="toolbar" aria-label="Действия с блоком"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}>
                <button onClick={() => onMove(idx, -1)} className="p-1 opacity-50 hover:opacity-100"
                  title="Выше" data-testid={`editorial-block-up-${b.id}`}>
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button onClick={() => onMove(idx, +1)} className="p-1 opacity-50 hover:opacity-100"
                  title="Ниже" data-testid={`editorial-block-down-${b.id}`}>
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button onClick={() => onDuplicate(b.id)} className="p-1 opacity-50 hover:opacity-100"
                  title="Дублировать" data-testid={`editorial-block-duplicate-${b.id}`}>
                  <Copy className="w-4 h-4" />
                </button>
                <button onClick={() => onToggleVisible(b)} className="p-1 opacity-50 hover:opacity-100"
                  title={b.is_visible ? "Скрыть" : "Показать"} data-testid={`editorial-block-visible-${b.id}`}>
                  {b.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button onClick={() => onRemove(b.id)} className="p-1 opacity-50 hover:opacity-100 text-red-600"
                  title="Удалить" data-testid={`editorial-block-remove-${b.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {isActive && (
              <div className="border-t border-espresso/10 px-3 pb-3 pt-3">
                {mode === "design" ? (
                  <>
                    <EditorFieldGuide type={b.master_type} />
                    <MasterEditor block={b} mode="design" detailedOpen={false}
                      onRequestDetailed={() => onOpenActive(b.id)} onReturnToCollected={() => onCloseActive(b.id)}
                      onPreview={onPreview} onEditBlock={onEditBlock} />
                  </>
                ) : (
                  <>
                    <MasterEditor block={b} mode="content" detailedOpen
                      onRequestDetailed={() => onOpenActive(b.id)} onReturnToCollected={() => onCloseActive(b.id)}
                      onPreview={onPreview} onEditBlock={onEditBlock}
                      onContinueInNextSpread={
                        b.master_type === "HERO"
                          ? (leftoverText) => onContinueHeroLeftover(b.id, leftoverText)
                          : undefined
                      } />

                    {showHeroNextStep && (
                      <HeroNextStepPanel
                        busy={continueBusyId === b.id}
                        onAdd={onQuickAddMaster}
                        onOpenLibrary={onAdd}
                      />
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
      <button onClick={onAdd}
        className="w-full py-2.5 rounded-xl border border-dashed border-espresso/20 text-sm text-espresso/70 hover:bg-white/40"
        data-testid="editorial-add-block">
        + Добавить разворот
      </button>
    </div>
  );
}

function HeroNextStepPanel({ busy, onAdd, onOpenLibrary }) {
  return (
    <div className="mt-4 rounded-xl border border-espresso/15 bg-cream/50 p-3"
      data-testid="editorial-hero-next-step">
      <div className="text-sm font-medium text-espresso">Обложка готова. Что добавить дальше?</div>
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" disabled={busy} onClick={() => onAdd("EDITORIAL_STORY")}
          className="rounded-lg border border-espresso/20 bg-white px-3 py-1.5 text-xs text-espresso disabled:opacity-50"
          data-testid="editorial-hero-next-editorial-story">
          Редакционная история
        </button>
        <button type="button" disabled={busy} onClick={() => onAdd("MACRO_SENSORY")}
          className="rounded-lg border border-espresso/20 bg-white px-3 py-1.5 text-xs text-espresso disabled:opacity-50"
          data-testid="editorial-hero-next-macro-sensory">
          Макро / сенсорные ноты
        </button>
        <button type="button" disabled={busy} onClick={() => onAdd("RECIPE")}
          className="rounded-lg border border-espresso/20 bg-white px-3 py-1.5 text-xs text-espresso disabled:opacity-50"
          data-testid="editorial-hero-next-recipe">
          Рецепт
        </button>
        <button type="button" onClick={onOpenLibrary}
          className="rounded-lg px-3 py-1.5 text-xs text-espresso/70 underline underline-offset-2"
          data-testid="editorial-hero-next-other">
          Выбрать другой MASTER
        </button>
      </div>
    </div>
  );
}

function DetailedEditorShell({ blockId, onClose, children }) {
  return (
    <div className="space-y-3" data-testid={`editorial-detailed-${blockId}`}>
      <div className="sticky top-[70px] z-20 flex justify-end rounded-xl border border-espresso/10 bg-white/95 px-3 py-2 shadow-sm backdrop-blur">
        <button type="button" onClick={onClose}
          className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-sm text-espresso"
          data-testid={`editorial-detailed-close-${blockId}`}>
          Вернуться к списку разворотов
        </button>
      </div>
      {children}
    </div>
  );
}

function MasterEditor({
  block, mode, detailedOpen, onRequestDetailed, onReturnToCollected, onPreview, onEditBlock,
  onContinueInNextSpread,
}) {
  const patch = (value) => onEditBlock(block.id, value);
  switch (block.master_type) {
    case "HERO": return <HeroEditor block={block} onPatch={patch} onContinueInNextSpread={onContinueInNextSpread} />;
    case "EDITORIAL_STORY": return <EditorialStoryEditor block={block} mode={mode}
      detailedOpen={detailedOpen} onRequestDetailed={onRequestDetailed}
      onReturnToCollected={onReturnToCollected} onRequestPreview={onPreview} onPatch={patch} />;
    case "VISUAL_STORY": return <VisualStoryEditor block={block} onPatch={patch} />;
    case "RECIPE": return <RecipeEditor block={block} mode={mode}
      detailedOpen={detailedOpen} onRequestDetailed={onRequestDetailed}
      onReturnToCollected={onReturnToCollected} onRequestPreview={onPreview} onPatch={patch} />;
    case "HISTORICAL_EDITORIAL": return <HistoricalEditorialEditor block={block} onPatch={patch} />;
    case "MACRO_SENSORY": return <MacroSensoryEditor block={block} onPatch={patch} />;
    case "PORTRAIT_DIALOGUE": return <PortraitDialogueEditor block={block} onPatch={patch} />;
    case "MACRO": return <MacroEditor block={block} onPatch={patch} />;
    case "PROCESS_STORY": return <ProcessStoryEditor block={block} onPatch={patch} />;
    case "STEP_BY_STEP": return <StepByStepEditor block={block} onPatch={patch} />;
    case "EDITORIAL_PAUSE": return <EditorialPauseEditor block={block} onPatch={patch} />;
    case "KNOWLEDGE": return <KnowledgeEditor block={block} onPatch={patch} />;
    default: return <BlockEditor block={block} onChange={patch} />;
  }
}

function MasterEditorLauncher({ block, label, hint, onOpenDetailed, onPreview }) {
  return (
    <div className="rounded-xl border border-espresso/10 bg-cream/35 p-3 sm:flex sm:items-center sm:justify-between sm:gap-4"
      data-testid={`editorial-master-launcher-${block.id}`} data-master-type={block.master_type}>
      <div className="min-w-0">
        <div className="font-serif text-lg text-espresso">{label}</div>
        <p className="mt-1 text-xs leading-relaxed text-espresso/65">{hint}</p>
      </div>
      <div className="mt-3 flex shrink-0 flex-wrap gap-2 sm:mt-0">
        <button type="button" onClick={onOpenDetailed}
          className="rounded-xl border border-espresso/20 bg-white px-3 py-2 text-sm text-espresso"
          data-testid={`editorial-content-detailed-${block.id}`}>Редактировать подробно</button>
        <button type="button" onClick={onPreview}
          className="rounded-xl bg-espresso px-3 py-2 text-sm text-cream"
          data-testid={`editorial-content-preview-${block.id}`}>Предпросмотр</button>
      </div>
    </div>
  );
}

function EditorFieldGuide({ type }) {
  const items = MASTER_FIELD_HELP[type] || [];
  if (!items.length) return null;
  return (
    <details className="editorial-field-guide mb-2 rounded-lg border border-espresso/10 bg-cream/35 px-3 py-1.5"
      data-testid={`editorial-field-help-${type}`}>
      <summary className="cursor-pointer text-[11px] text-espresso/70 select-none">Подсказки к полям</summary>
      <div className="grid gap-1.5 pt-2 sm:grid-cols-2">
        {items.map(([label, text]) => (
          <p key={label} className="text-[11px] leading-snug text-espresso/70">
            <strong className="font-medium text-espresso">{label}:</strong> {text}
          </p>
        ))}
        <p className="text-[11px] leading-snug text-espresso/70 sm:col-span-2">
          <strong className="font-medium text-espresso">Описание изображения:</strong>{" "}
          Кратко опишите, что изображено на фотографии. Этот текст не отображается поверх изображения и помогает доступности.
        </p>
      </div>
    </details>
  );
}

// ---------- Block editor (structured fields) ----------
function BlockEditor({ block, onChange }) {
  const c = block.content || {};
  const patchContent = (patch) => onChange({ content: { ...c, ...patch } });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <label className="block text-[10px] uppercase tracking-widest opacity-60 mb-1">Надзаголовок</label>
        <Input value={c.eyebrow || ""} onChange={(e) => patchContent({ eyebrow: e.target.value })} />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-widest opacity-60 mb-1">Заголовок</label>
        <Input value={c.headline || ""} onChange={(e) => patchContent({ headline: e.target.value })}
          data-testid={`editorial-field-headline-${block.id}`} />
      </div>
      <div className="md:col-span-2">
        <label className="block text-[10px] uppercase tracking-widest opacity-60 mb-1">Лид</label>
        <textarea value={c.lead || ""} rows={2}
          onChange={(e) => patchContent({ lead: e.target.value })}
          className="w-full border rounded px-2 py-1.5 text-sm italic" />
      </div>
      <div className="md:col-span-2">
        <label className="block text-[10px] uppercase tracking-widest opacity-60 mb-1">Основной текст</label>
        <textarea value={c.body || ""} rows={6}
          onChange={(e) => patchContent({ body: e.target.value })}
          className="w-full border rounded px-2 py-2 text-sm font-serif leading-relaxed"
          data-testid={`editorial-field-body-${block.id}`} />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-widest opacity-60 mb-1">Цитата</label>
        <Input value={c.quote || ""} onChange={(e) => patchContent({ quote: e.target.value })} />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-widest opacity-60 mb-1">Подпись</label>
        <Input value={c.caption || ""} onChange={(e) => patchContent({ caption: e.target.value })} />
      </div>
      <div className="md:col-span-2">
        <label className="block text-[10px] uppercase tracking-widest opacity-60 mb-1">Изображения из Медиатеки · идентификаторы через запятую</label>
        <Input value={(c.media_ids || []).join(", ")}
          onChange={(e) => patchContent({ media_ids: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
          placeholder="Добавьте идентификаторы выбранных изображений" />
      </div>
    </div>
  );
}

// ---------- MASTER library modal ----------
function MasterLibrary({ masters, onClose, onPick }) {
  const [helpType, setHelpType] = useState(null);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="editorial-master-library">
      <div className="w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-cream rounded-2xl p-4 md:p-5">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-60">Библиотека MASTER</div>
            <div className="font-serif text-2xl text-espresso">Выберите композицию разворота</div>
          </div>
          <button onClick={onClose} className="text-sm opacity-70 hover:opacity-100">Закрыть</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
          {masters.map((m) => {
            const open = helpType === m.key;
            return (
              <div key={m.key} className="relative rounded-xl bg-white border hairline p-3 hover:bg-sand/20 transition"
                data-testid={`editorial-master-${m.key}`}>
                <button onClick={() => onPick(m.key)} className="w-full text-left">
                  <MasterPlaceholder type={m.key} />
                  <div className="font-serif text-base text-espresso mt-2 pr-8">{m.label}</div>
                  <div className="text-[11px] leading-snug opacity-70 mt-0.5 min-h-[28px]">{m.hint}</div>
                </button>
                <button type="button" onClick={() => setHelpType(open ? null : m.key)}
                  className="absolute right-2.5 top-[76px] inline-flex h-7 w-7 items-center justify-center rounded-full border border-espresso/20 bg-cream text-espresso hover:bg-sand/70"
                  aria-label={`Как использовать: ${m.label}`}
                  data-testid={`editorial-master-help-${m.key}`}>
                  <HelpCircle className="h-4 w-4" />
                </button>
                {open && (
                  <div className="mt-2 rounded-lg border border-espresso/10 bg-cream/80 p-2.5 text-xs leading-relaxed text-espresso"
                    data-testid={`editorial-master-help-text-${m.key}`}>
                    <div className="mb-0.5 font-medium">Как использовать</div>
                    {MASTER_USAGE_HELP[m.key] || m.hint}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// simple visual placeholder per MASTER type
function MasterPlaceholder({ type }) {
  const box = "bg-espresso/10 rounded";
  const line = "bg-espresso/15 rounded h-1.5";
  const cfg = {
    HERO:                 <><div className={`${box} h-10 mb-1`} /><div className={`${line} w-2/3`} /></>,
    EDITORIAL_STORY:      <><div className={`${line} w-1/2 mb-1`} /><div className={`${line} w-full mb-1`} /><div className={`${line} w-full mb-1`} /><div className={`${line} w-4/5`} /></>,
    VISUAL_STORY:         <div className="grid grid-cols-3 gap-1"><div className={`${box} h-10`} /><div className={`${box} h-10`} /><div className={`${box} h-10`} /></div>,
    MACRO:                <div className={`${box} h-12`} />,
    MACRO_SENSORY:        <div className="grid grid-cols-[3fr_2fr] gap-1 h-12"><div className={box} /><div className="flex flex-col justify-center gap-1"><div className={`${line} w-2/3`} /><div className={`${line} w-full`} /><div className={`${line} w-4/5`} /><div className={`${line} w-full`} /></div></div>,
    PORTRAIT_DIALOGUE:    <div className="grid grid-cols-[2fr_3fr] gap-1 h-12"><div className={box} /><div className="flex flex-col justify-center gap-1"><div className={`${line} w-3/4`} /><div className={`${line} w-full`} /><div className={`${line} w-5/6`} /><div className={`${line} w-2/3`} /></div></div>,
    PROCESS_STORY:        <><div className={`${line} w-1/4 mb-1`} /><div className={`${line} w-full mb-1`} /><div className={`${box} h-8 mb-1`} /><div className={`${line} w-3/4`} /></>,
    STEP_BY_STEP:         <div className="space-y-1">{[1,2,3].map(i=>(<div key={i} className="flex gap-1"><div className={`${box} w-6 h-4`} /><div className={`${line} flex-1 mt-1.5`} /></div>))}</div>,
    EDITORIAL_PAUSE:      <div className="flex items-center justify-center h-12"><div className={`${line} w-1/2 italic`} /></div>,
    RECIPE:               <div className="grid grid-cols-[1fr_2fr] gap-1"><div className="space-y-1">{[1,2,3].map(i=>(<div key={i} className={`${line} w-full`} />))}</div><div className="space-y-1">{[1,2,3,4].map(i=>(<div key={i} className={`${line} w-full`} />))}</div></div>,
    KNOWLEDGE:            <div className="grid grid-cols-4 gap-0.5">{[...Array(8)].map((_,i)=>(<div key={i} className={`${box} h-3`} />))}</div>,
    HISTORICAL_EDITORIAL: <><div className={`${line} w-1/3 mb-1 italic`} /><div className={`${line} w-full mb-1`} /><div className={`${line} w-full mb-1`} /><div className={`${line} w-2/3`} /></>,
  };
  return <div className="h-16 p-2 bg-cream rounded">{cfg[type] || <div className={box + " h-12"} />}</div>;
}

// ---------- Preview shell ----------
function PreviewShell({ blocks, material, masters, mode }) {
  const visible = (blocks || []).filter((b) => b.is_visible);
  const masterLabel = (k) => masters.find((m) => m.key === k)?.label || k;
  return (
    <div className="flex justify-center py-4" data-testid={`editorial-preview-${mode}`}>
      <div
        className={`${mode === "mobile" ? "w-full max-w-[393px]" : "w-full max-w-[1280px]"} overflow-hidden rounded-2xl border bg-white hairline`}
        style={{ minHeight: 600 }}>
        <div className="px-8 py-6 border-b border-espresso/10">
          <div className="text-[10px] uppercase tracking-widest opacity-60">
            Предпросмотр — {mode === "mobile" ? "телефон" : "компьютер"}
          </div>
        </div>
        <div className="divide-y divide-espresso/10">
          {visible.length === 0 && <div className="opacity-60 text-sm px-8 py-6">Нет видимых разворотов.</div>}
          {visible.map((b) => (
            b.master_type === "HERO" ? (
              <HeroRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "EDITORIAL_STORY" ? (
              <EditorialStoryRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "VISUAL_STORY" ? (
              <VisualStoryRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "RECIPE" ? (
              <RecipeRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "HISTORICAL_EDITORIAL" ? (
              <HistoricalEditorialRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "MACRO_SENSORY" ? (
              <MacroSensoryRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "PORTRAIT_DIALOGUE" ? (
              <PortraitDialogueRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "MACRO" ? (
              <MacroRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "PROCESS_STORY" ? (
              <ProcessStoryRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "STEP_BY_STEP" ? (
              <StepByStepRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "EDITORIAL_PAUSE" ? (
              <EditorialPauseRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "KNOWLEDGE" ? (
              <KnowledgeRender key={b.id} block={b} mode={mode} />
            ) : (
              <section key={b.id} className="px-8 py-8"
                data-testid={`editorial-preview-block-${b.id}`}>
                <div className="text-[9px] uppercase tracking-widest opacity-40 mb-2">
                  {masterLabel(b.master_type)}
                </div>
                {b.content?.eyebrow && (
                  <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">{b.content.eyebrow}</div>
                )}
                {b.content?.headline && (
                  <h2 className="font-serif text-2xl md:text-3xl text-espresso mb-3">{b.content.headline}</h2>
                )}
                {b.content?.lead && (
                  <p className="italic opacity-80 mb-3">{b.content.lead}</p>
                )}
                {b.content?.body && (
                  <div className="text-[15px] leading-relaxed whitespace-pre-wrap">{b.content.body}</div>
                )}
                {b.content?.quote && (
                  <blockquote className="border-l-2 border-espresso/30 pl-4 italic mt-4 font-serif text-xl">
                    {b.content.quote}
                  </blockquote>
                )}
                {b.content?.caption && (
                  <div className="text-xs italic opacity-60 mt-2">{b.content.caption}</div>
                )}
              </section>
            )
          ))}
        </div>
      </div>
    </div>
  );
}
